'use client';

import * as React from 'react';
import { motion } from 'framer-motion';
import { Video, CalendarClock, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SNAPPY, cascadeItem } from '@/lib/motion';
import type { MeetingKind } from '@/features/meetings';

const MODES: {
  kind: MeetingKind;
  title: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    kind: 'instant',
    title: 'Start instant',
    description: 'Spin up a room now and share the link.',
    icon: <Video className="h-6 w-6" aria-hidden="true" />,
  },
  {
    kind: 'scheduled',
    title: 'Schedule',
    description: 'Pick a day and time — no calendar app.',
    icon: <CalendarClock className="h-6 w-6" aria-hidden="true" />,
  },
];

/**
 * Two wide, full-width option cards — one tap, not tab-then-act (§4.1). The
 * deliberate design choice for this screen: a horizontal icon → label →
 * affordance lockup makes each a large, thumb-easy target that fills the
 * column, with a Snappy tactile press and a soft brand wash on hover. The
 * whole card is the target and it works keyboard-first.
 */
export function ModeCards({ onSelect }: { onSelect: (kind: MeetingKind) => void }) {
  return (
    <div className="flex flex-col gap-3.5">
      {MODES.map((mode) => (
        <motion.button
          key={mode.kind}
          type="button"
          variants={cascadeItem}
          onClick={() => onSelect(mode.kind)}
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.99 }}
          transition={SNAPPY}
          aria-label={mode.kind === 'instant' ? 'Start an instant meeting' : 'Schedule a meeting'}
          className={cn(
            'group relative flex w-full items-center gap-4 overflow-hidden rounded-[var(--radius-lg)] border p-4 text-left sm:gap-5 sm:p-5',
            'border-[var(--border)] bg-[var(--surface)] shadow-[var(--shadow-sm)]',
            'transition-[border-color,box-shadow] duration-200',
            'hover:border-[color-mix(in_srgb,var(--primary)_45%,transparent)] hover:shadow-[var(--shadow-md)]',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)]',
            'motion-reduce:hover:translate-y-0',
          )}
        >
          {/* soft brand wash that fades in on hover */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[var(--primary-soft)] to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-60"
          />
          <span className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-[var(--primary-soft)] text-[var(--primary)] sm:h-14 sm:w-14">
            {mode.icon}
          </span>
          <span className="relative min-w-0 flex-1 space-y-0.5">
            <span className="block font-heading text-base font-semibold text-[var(--text-primary)] sm:text-lg">
              {mode.title}
            </span>
            <span className="block text-sm text-[var(--text-muted)]">{mode.description}</span>
          </span>
          <ArrowRight
            className="relative ml-auto h-5 w-5 shrink-0 -translate-x-1.5 text-[var(--primary)] opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100 motion-reduce:transition-none"
            aria-hidden="true"
          />
        </motion.button>
      ))}
    </div>
  );
}
